import { EmployerFooter, EmployerHeader } from '@seek/adv-header-footer';
import type { Language } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { useToast, TextLink, Text, Box } from 'braid-design-system';
import { differenceInDays } from 'date-fns';
import { observer } from 'mobx-react';
import { type FC, useEffect, useState } from 'react';

import { ErrorContent } from 'src/App/ActionsPage/ErrorScreen/ErrorContent'; // FIXME: CM page shouldn't import from ActionsPage
import { useFeatureFlag } from 'src/App/CandidateManagementPage/FeatureFlagsContext';
import { UnhandledErrorPageBlock } from 'src/components/app';
import AppErrorBoundary from 'src/components/app/AppErrorBoundary';
import {
  UserActionStatusProvider,
  useUserActionStatus,
} from 'src/components/common/UserActionStatusContext';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import type { HeaderFooterContextQuery } from 'src/libs/HeaderFooterContext';
import type { PageConfig } from 'src/libs/PageConfig/types';
import authServiceFactory from 'src/services/authServiceFactory';
import { environments } from 'src/siteConfig';
import { getAdvertiserIds, isValidId } from 'src/utils/advertiserIds';
import {
  getAnzCsPhoneNumberFromSite,
  getHelpCentreUrlFromSite,
} from 'src/utils/getCsContactMethodMessageFromSite';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';
import { useStores } from 'src/utils/hooks/useStores';
import { triggerHotjarPoll } from 'src/utils/hotjar';
import { isANZSite } from 'src/utils/site';

import {
  CandidateManagementApp,
  type CandidateManagementAppProps,
} from '../../App/AppPageBlock';
import { CandidateManagementAppSkeleton } from '../../App/AppPageBlock/CandidateManagementAppSkeleton';
import { PageAlert } from '../../App/AppPageBlock/PageAlert';
import { PageBlock } from '../../App/AppPageBlock/PageBlock';
import {
  createPageAnalytics,
  type CandidatesManagementPageAnalytics,
} from '../../App/AppPageBlock/createPageAnalytics';
import type {
  JobPlacementStatus,
  ResponseErrorReason,
} from '../../App/AppPageBlock/graphql/hirer-types.generated';
import { CandidateManagementLayout } from '../../App/CandidateManagementPage/CandidateManagementPage';
import { AccessType } from '../../stores/JobStore/types';
import type { IStore } from '../../stores/Store';
import useQueryParamsStore from '../../utils/hooks/useQueryParamsStore';

import translations from './.vocab';
import fetchInitialJobApplications from './fetchInitialJobApplications';
import {
  navigateToAccountSelectionPage,
  navigateToJobListPage,
  navigateToSignInPageWithReturnUrl,
} from './utils/utils';

import * as styles from './CandidateManagement.css';

interface Props {
  dataDogRum: PageConfig['dataDogRum'];
  pageAnalytics: CandidatesManagementPageAnalytics;
  isMobile: boolean;
  headerFooterContextData: HeaderFooterContextQuery;
  setStateAnalytics: React.Dispatch<
    React.SetStateAction<CandidatesManagementPageAnalytics>
  >;
  seekAdvertiserId: string;
}

interface AccessAndJob {
  /**
   * @deprecated
   * Legacy fields below are here because they come from MobX types
   * Ideally IDs should be treated as arbitrary values, so they should just be strings
   */
  legacy: {
    advertiserId: string; // FIXME: this is picked from windows, not safe. We should use data from Hirer Graph istead
    jobId: number;
    positionId: number;
    userId: number;
  };
  job: {
    id: string;
    productDisplayName?: string | null;
    canPurchaseReferenceChecks: boolean;
    canBeUpgraded: boolean;
    canShowHomeLocationFilterInCandidateManagement: boolean;
    canShowNationalityFilterInCandidateManagement: boolean;
    isIndirectJobButCanBeChanged: boolean;
    billableApplicants:
      | {
          __typename?: 'BillableApplicantsResponseSuccess';
          data?: {
            __typename?: 'BillableApplicantsData';
            billableApplicantsCount:
              | {
                  __typename?: 'BillableApplicantsCountResponseSuccess';
                  result: number;
                }
              | {
                  __typename?: 'ResponseError';
                  error: ResponseErrorReason;
                };
            maxBillableApplicants:
              | {
                  __typename?: 'MaxBillableApplicantsResponseSuccess';
                  result: number;
                }
              | {
                  __typename?: 'ResponseError';
                  error: ResponseErrorReason;
                };
          } | null;
        }
      | { __typename?: 'ResponseError'; error: ResponseErrorReason };
    placementV2:
      | {
          __typename?: 'JobPlacementV2ResponseSuccess';
          result: {
            __typename?: 'JobPlacementV2';
            canShowPlacementPrompt: boolean;
            status?: JobPlacementStatus | null;
          };
        }
      | {
          __typename?: 'ResponseError';
          error: ResponseErrorReason;
        };
    highFitCount:
      | {
          __typename?: 'HighFitCountResponseSuccess';
          result: number;
        }
      | {
          __typename?: 'ResponseError';
          error: ResponseErrorReason;
        };
  };
  permissions: {
    canExportApplicationsToCsv: boolean;
  };
}

/**
 * CandidateManagementInitialiser
 * @description This component handles the first load of the app e.g. auth, job details, etc.
 */
export const CandidateManagementInitialiser: FC<Props> = observer(
  ({
    dataDogRum,
    pageAnalytics,
    isMobile,
    headerFooterContextData,
    setStateAnalytics,
    seekAdvertiserId,
  }) => {
    const store = useStores();
    const [query, setQuery] = useQueryParamsStore();
    const showToast = useToast();
    const { t } = useTranslations(translations);
    const [hasLoginRedirectError, setHasLoginRedirectError] = useState(false);
    const { language, site, locale, environment } = useAppConfig();
    const [accessAndJobState, setAccessAndJobState] = useState<
      | {
          ok: true;
          data: AccessAndJob;
        }
      | { ok: false }
      | undefined
    >();
    const { variant: highFitExperimentVariant } = useFeatureFlag(
      'cm_high_fit_applicant_explainability',
    );
    const isStaging = environment === environments.staging;

    useEffect(() => {
      if (!accessAndJobState) {
        const { advertiserId } = getAdvertiserIds();

        if (!isValidId(advertiserId)) {
          const authService = authServiceFactory();

          authService
            .loginWithRedirect()
            .then(() => {})
            .catch((error) => {
              dataDogRum.addCustomError({
                error,
                context: {
                  activity: 'LOGIN_WITH_REDIRECTION_URL',
                  description: 'Error attempting: loginWithRedirect',
                },
              });
              setHasLoginRedirectError(true);
            });
          return;
        }

        let accessAndGetJobProps: AccessAndGetJobProps;
        if (query.positionId) {
          accessAndGetJobProps = {
            store,
            jobIdType: 'positionId',
            expectedJobId: query.positionId,
            language,
            selectedProspectId: query.selectedProspectId,
            addCustomError: dataDogRum.addCustomError,
          };
        } else if (query.jobId) {
          accessAndGetJobProps = {
            store,
            jobIdType: 'jobId',
            expectedJobId: query.jobId,
            language,
            selectedProspectId: query.selectedProspectId,
            addCustomError: dataDogRum.addCustomError,
          };
        } else {
          return navigateToJobListPage();
        }

        checkAccessAndFetchJobInformation(accessAndGetJobProps)
          .then((result) => {
            if (result.success) {
              const job = result.jobInfo;

              let canExportApplicationsToCsv = false;
              if (
                headerFooterContextData.headerFooterContext?.advertiser
                  ?.permissionOptions.canExportApplications.__typename ===
                'CanExportApplicationsResponseSuccess'
              ) {
                canExportApplicationsToCsv =
                  headerFooterContextData.headerFooterContext?.advertiser
                    .permissionOptions.canExportApplications.result;
              }

              if (
                job.referenceCheckBalance.__typename ===
                  'JobReferenceCheckBalanceResponseSuccess' &&
                job.referenceCheckBalance.result?.__typename !==
                  'JobReferenceCheckExpired'
              ) {
                triggerHotjarPoll('cm_seekref_poll');
              }

              const highFitCountResult =
                job.highFitCount.__typename === 'HighFitCountResponseSuccess'
                  ? job.highFitCount.result
                  : undefined;

              const jobAgeInDays = differenceInDays(
                new Date(),
                job.createdDate,
              );

              if (
                highFitCountResult &&
                highFitCountResult >= 1 &&
                jobAgeInDays >= 8
              ) {
                if (highFitExperimentVariant === 'control') {
                  triggerHotjarPoll('cm_no_highfit_displayed');
                }
                if (highFitExperimentVariant === 'challenger') {
                  triggerHotjarPoll('cm_highfit_displayed');
                }
              }

              setAccessAndJobState({
                ok: true,
                data: {
                  job: {
                    id: job.id,
                    canBeUpgraded: job.canBeUpgraded,
                    canPurchaseReferenceChecks: job.canPurchaseReferenceChecks,
                    canShowHomeLocationFilterInCandidateManagement:
                      job.canShowHomeLocationFilterInCandidateManagement,
                    canShowNationalityFilterInCandidateManagement:
                      job.canShowNationalityFilterInCandidateManagement,
                    billableApplicants: job.billableApplicants,
                    placementV2: job.placementV2,
                    isIndirectJobButCanBeChanged:
                      job.isIndirectJobButCanBeChanged,
                    highFitCount: job.highFitCount,
                  },
                  permissions: {
                    canExportApplicationsToCsv,
                  },
                  legacy: {
                    advertiserId,
                    positionId: store.jobStore.job.jobInformation.positionId, // FIXME: Shouldn't reach into the store here. This will go away when we get rid of positionId
                    userId: store.userStore.user.userId || 0, // FIXME: Shouldn't reach into the store here, must use data from HeaderFooterContext
                    jobId: store.jobStore.job.jobInformation.jobId!, // FIXME: For legacy reason, this needs to be a number, instead of a string like job.id. So we use MobX here
                  },
                },
              });
            } else {
              setAccessAndJobState({ ok: false });
            }
          })
          .catch((error) => {
            dataDogRum.addCustomError({
              error,
              context: {
                activity: 'FETCH_ACCESS_AND_JOB_INFORMATION',
                description: 'Error attempting: fetchInitialJobApplications',
              },
            });
            store.jobStore.setIsError();
            setAccessAndJobState({ ok: false });
          });
      }
    }, [
      accessAndJobState,
      query.positionId,
      query.page,
      query.search,
      query,
      language,
      store,
      dataDogRum,
      headerFooterContextData,
      highFitExperimentVariant,
    ]);

    useEffect(() => {
      if (query.referrer === 'edit-page-success') {
        showToast({
          message: t('Your job has been updated'),
          tone: 'positive',
          closeLabel: t('close'),
        });
        setQuery.clearReferrer();
      }
    }, [showToast, query.referrer, setQuery, t]);

    const loadedEmployerHeader = (
      <div className={styles.header}>
        <EmployerHeader
          activeTabId="jobs"
          site={site}
          locale={locale}
          isStaging={isStaging}
          identityContext={
            headerFooterContextData.headerFooterContext?.identityContext
          }
          advertiser={headerFooterContextData.headerFooterContext?.advertiser}
          user={headerFooterContextData.headerFooterContext?.user}
          data-testid="employer-header"
          divider
          onTrackedItemInteraction={(event) => {
            pageAnalytics.adapter.link({
              event: event.eventName,
              attributes: { ...event },
            });
          }}
        />
      </div>
    );

    const renderContentInLayout = ({
      content,
    }: {
      content: JSX.Element;
    }): JSX.Element => (
      <CandidateManagementLayout
        header={isMobile ? null : loadedEmployerHeader}
        content={content}
        footer={
          <EmployerFooter
            site={site}
            locale={locale}
            isStaging={isStaging}
            identityContext={
              headerFooterContextData.headerFooterContext?.identityContext
            }
            advertiser={headerFooterContextData.headerFooterContext?.advertiser}
          />
        }
      />
    );

    if (hasLoginRedirectError) {
      return renderContentInLayout({
        content: (
          <ErrorContent
            errorCode="500"
            heading={t('Can we try that again?')}
            body={
              <Text tone="secondary">
                {t("Retry the page to see your candidate's profile", {
                  Link: (children) => (
                    <TextLink href={window.location.href}>{children}</TextLink>
                  ),
                })}
              </Text>
            }
          />
        ),
      });
    }

    if (!accessAndJobState) {
      return renderContentInLayout({
        content: (
          <AppErrorBoundary>
            <CandidateManagementAppSkeleton />
          </AppErrorBoundary>
        ),
      });
    }

    // FIXME: MobX should report access issues to React state, so we don't have to reach in like this
    if (!store.jobStore.hasAccess) {
      const csPhoneNumber = getAnzCsPhoneNumberFromSite(site);

      const noAccessToJobMessage = isANZSite(site)
        ? t(
            'If you think you should be one of them, ask your system administrator...',
            {
              TelLink: () => (
                <TextLink href={`tel:${csPhoneNumber}`}>
                  {csPhoneNumber}
                </TextLink>
              ),
            },
          )
        : t(
            'If you think you should be one of them, ask your system administrator - with Customer Service link',
            {
              Link: (children) => (
                <TextLink href={getHelpCentreUrlFromSite(site, language)}>
                  {children}
                </TextLink>
              ),
            },
          );
      return renderContentInLayout({
        content: (
          <PageBlock>
            <PageAlert>
              {t('Only some people can see this page')}
              <br />
              {noAccessToJobMessage}
            </PageAlert>
          </PageBlock>
        ),
      });
    }

    if (!accessAndJobState.ok || store.jobStore.isError) {
      return renderContentInLayout({
        content: (
          <PageBlock>
            <Box marginY="small" marginX="gutter">
              <UnhandledErrorPageBlock />
            </Box>
          </PageBlock>
        ),
      });
    }

    return (
      <UserActionStatusProvider
        jobId={accessAndJobState.data.job.id}
        userId={accessAndJobState.data.legacy.userId}
      >
        <CandidateManagementInitApplications
          seekAdvertiserId={seekAdvertiserId}
          accessAndJob={accessAndJobState.data}
          dataDogRum={dataDogRum}
          pageAnalytics={pageAnalytics}
          setStateAnalytics={setStateAnalytics}
          loadedEmployerHeader={loadedEmployerHeader}
          renderContentInLayout={renderContentInLayout}
          store={store}
        />
      </UserActionStatusProvider>
    );
  },
);
interface AccessAndGetJobProps {
  store: IStore;
  jobIdType: 'jobId' | 'positionId';
  expectedJobId: number; // either jobId or PositionId
  language: Language;
  selectedProspectId?: string;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
}

const checkAccessAndFetchJobInformation = async ({
  store,
  jobIdType,
  expectedJobId,
  language,
  addCustomError,
}: AccessAndGetJobProps) => {
  const jobInfo = await store.jobStore.loadHirerJobDetailsAndAccess(
    jobIdType,
    String(expectedJobId),
    language,
    addCustomError,
  );

  if (store.jobStore.accessType === AccessType.INCORRECT_ADVERTISER) {
    navigateToAccountSelectionPage();
  }

  if (!jobInfo) {
    return { success: false } as const;
  }

  return { success: true, jobInfo } as const;
};

/**
 * CandidateManagementInitApplications
 * @description This component handles the _initial load_ of applications. Subsequent loads will be handled separately, in AppPageBlock
 */
const CandidateManagementInitApplications: FC<{
  seekAdvertiserId: string;
  accessAndJob: AccessAndJob;
  dataDogRum: PageConfig['dataDogRum'];
  pageAnalytics: CandidatesManagementPageAnalytics;
  setStateAnalytics: React.Dispatch<
    React.SetStateAction<CandidatesManagementPageAnalytics>
  >;
  loadedEmployerHeader: JSX.Element;
  renderContentInLayout: (params: { content: JSX.Element }) => JSX.Element;
  store: IStore;
}> = observer(
  ({
    seekAdvertiserId,
    accessAndJob,
    pageAnalytics,
    setStateAnalytics,
    dataDogRum,
    loadedEmployerHeader,
    renderContentInLayout,
    store,
  }) => {
    const [query, setQuery] = useQueryParamsStore();
    const { getMustHaveFilterState } = useUserActionStatus(
      'must-have-filter-state',
    );
    const showToast = useToast();
    const { getLabelFromBucket } = useBucketLabel();
    const { t } = useTranslations(translations);
    const messages = handleEasyAccessMessages(t);
    const { language, site, locale, environment } = useAppConfig();
    const [initialLoadResponse, setInitialLoadResponse] = useState<
      CandidateManagementAppProps['initialLoadResponse'] | undefined
    >();

    const { variant: jobPlacementExperimentVariant } = useFeatureFlag(
      'cm_improve_direct_placement_signals',
    );

    const { variant: highFitApplicationExperimentVariant } = useFeatureFlag(
      'cm_high_fit_applicant_explainability',
    );

    useEffect(() => {
      if (!initialLoadResponse) {
        const { job } = accessAndJob;
        const highFitCountResult =
          job.highFitCount.__typename === 'HighFitCountResponseSuccess'
            ? job.highFitCount.result
            : 0;

        fetchInitialJobApplications(
          store,
          (page: number | undefined) => setQuery.updatePage(page),
          accessAndJob.legacy.advertiserId,
          seekAdvertiserId,
          accessAndJob.legacy.positionId,
          accessAndJob.legacy.jobId,
          query,
          showToast,
          getLabelFromBucket,
          messages,
          language,
          dataDogRum.addCustomError,
          getMustHaveFilterState(),
          jobPlacementExperimentVariant,
          highFitCountResult > 0
            ? {
                experimentVariant: highFitApplicationExperimentVariant,
                highFitApplicantRatio: `${highFitCountResult}:undefined`,
              }
            : { experimentVariant: 'ineligible' },
        )
          .then(() => {
            setInitialLoadResponse({
              ok: true,
              jobId: accessAndJob.legacy.jobId,
              canBeUpgraded: job.canBeUpgraded,
              canPurchaseReferenceChecks: job.canPurchaseReferenceChecks,
              canExportApplicationsToCsv:
                accessAndJob.permissions.canExportApplicationsToCsv,
              canShowHomeLocationFilterInCandidateManagement:
                job.canShowHomeLocationFilterInCandidateManagement,
              canShowNationalityFilterInCandidateManagement:
                job.canShowNationalityFilterInCandidateManagement,
              isIndirectJobButCanBeChanged: job.isIndirectJobButCanBeChanged,
              placementV2: job.placementV2,
              highFitCount: job.highFitCount,
            });

            const getAdProductName = () => {
              if (!job.productDisplayName) {
                return 'Unknown';
              }
              // FIXME: this doesn't seem like the right check? job.billableApplicants is always truthy
              if (job.billableApplicants) {
                return 'premium pay per application';
              }

              return job.productDisplayName;
            };

            setStateAnalytics(
              createPageAnalytics({
                locale,
                site,
                environment,
                userId: accessAndJob.legacy.userId,
                seekAdvertiserId,
                extraBaseAttributes: {
                  seekListingId: accessAndJob.legacy.jobId,
                  adProductName: getAdProductName(),
                  currentPage: 'candidate list',
                  testingId: 'loyalty-beta-experience',
                },
              }),
            );
          })
          .catch((error) => {
            if (error.message === 'UNAUTHENTICATED') {
              navigateToSignInPageWithReturnUrl();
              setInitialLoadResponse({ ok: false });
              return;
            }
            dataDogRum.addCustomError({
              error,
              context: {
                activity: 'FETCH_INITIAL_JOB_APPLICATIONS',
                description: 'Error attempting: fetchInitialJobApplications',
              },
            });
            setInitialLoadResponse({ ok: false });
          });
      }
    }, [
      initialLoadResponse,
      seekAdvertiserId,
      dataDogRum,
      environment,
      getLabelFromBucket,
      getMustHaveFilterState,
      accessAndJob,
      language,
      locale,
      messages,
      query,
      setQuery,
      setStateAnalytics,
      showToast,
      site,
      store,
      jobPlacementExperimentVariant,
      highFitApplicationExperimentVariant,
    ]);

    if (!initialLoadResponse) {
      return renderContentInLayout({
        content: (
          <AppErrorBoundary>
            <CandidateManagementAppSkeleton />
          </AppErrorBoundary>
        ),
      });
    }

    return renderContentInLayout({
      content: (
        <AppErrorBoundary>
          <CandidateManagementApp
            employerHeader={loadedEmployerHeader}
            initialLoadResponse={initialLoadResponse}
            setInitialLoadResponse={setInitialLoadResponse}
            pageAnalytics={pageAnalytics}
            dataDogRum={dataDogRum}
            jobPlacementExperimentVariant={jobPlacementExperimentVariant}
            highFitApplicationExperimentVariant={
              highFitApplicationExperimentVariant
            }
          />
        </AppErrorBoundary>
      ),
    });
  },
);

type TranslateFn = ReturnType<typeof useTranslations>['t'];

export interface HandleEasyAccessTranslations {
  'application has been moved': ({
    firstName,
    bucket,
  }: {
    firstName: string;
    bucket: string;
  }) => string;
  close: () => string;
}

const handleEasyAccessMessages = (
  t: TranslateFn,
): HandleEasyAccessTranslations => ({
  'application has been moved': ({ firstName, bucket }) =>
    t('application has been moved', { firstName, bucket }) as string,
  close: () => t('close') as string,
});
